import { AxiosError, AxiosResponse, AxiosInstance } from 'axios'
import { action, observable, reaction, makeObservable, runInAction } from 'mobx'
import { FormApi } from 'final-form'
import { getClient } from 'api'
import { pick } from 'dot-object'
import TraversalUpdater from 'api/interceptors/traversalUpdater'
import {
  broadcastToParent,
  RegisteredEventName
} from '@finanzcheck/catalyst-pollard'
import ConfigurationError from 'error/configurationError'
import ApiError from 'error/apiError'
import TraversalCreator from 'api/interceptors/traversalCreator'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import TraversalRecovery from 'api/interceptors/traversalRecovery'
import { logInfo } from 'utils/log'
import { isTenantSmava } from 'utils/url'
import { getBrowserParentOrigin } from 'utils/pollard/parentOrigin'

import getInitialFormValues from 'formInitialValues'
import {
  FailuresItem,
  Patch,
  RootObject,
  Data,
  CreditChangeOption
} from 'interfaces/ApolloResponse.interface'
import { logAndTrackError } from 'utils/log/logAndTrackError'
import { addConditionalPatches } from 'utils/patch'
import reactions from './helpers/reactions'
import type RootStore from 'stores/RootStore'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'
import { HttpStatusCode } from 'utils/url/status'
import { getFailuresOfAxiosResponse } from 'stores/utils/helpers/getFailuresOfAxiosResponse'
import { mergeDeepRightArraySafe } from 'utils/array'
import type {
  FrameHostData,
  FieldChange,
  TraversalResponse
} from './TraversalStore.types'
import {
  postCreateTraversal,
  getTraversal,
  patchTraversal,
  putTraversal,
  postResumeTraversal,
  postCheckTenantEmail
} from './helpers/api/traversalApi'
import {
  trackValidationErrors,
  trackValidationSuccess,
  trackTraversalEvent,
  sendUserPropertiesOnPut
} from './helpers/tracking/traversalTracking'
import {
  getRemovablePaths,
  removePathsFromData,
  buildDacTransactionPatches,
  buildPutPayloadPaths,
  shouldResetAmountAdaption
} from './helpers/payload/traversalPayload'
import {
  executeFinalize,
  executeGoToFinal
} from './helpers/finalize/finalizeTraversal'
import {
  applyFieldChange,
  applyMultipleFieldChanges,
  pickListItem,
  buildListWithAddedItem,
  buildListWithRemovedItem
} from './helpers/form/traversalFields'

export type { FrameHostData, FieldChange }

const allowedPathUpdates = [
  'system',
  'debtors.primary.personId',
  'debtors.secondary.personId'
]

const isSmava = isTenantSmava()

class TraversalStore {
  private rootStore: RootStore
  private apolloClient: AxiosInstance
  private form?: FormApi

  // Core observables — the traversal identity and its API payload.
  // All other observable state lives in TraversalSessionStore (this.meta).
  public traversalId: string | null = null
  public data: Data = {} as Data

  /** Non-observable: guards against duplicate traversal creation. */
  public isTraversalCreationInProgress: boolean

  private setInterceptors = () => {
    TraversalCreator.create(this).apply(this.apolloClient)
    TraversalRecovery.create(this).apply(this.apolloClient)
    TraversalUpdater.create(this, allowedPathUpdates).apply(this.apolloClient)
  }

  constructor(rootStore: RootStore) {
    makeObservable(this, {
      traversalId: observable,
      data: observable,
      setTraversalDataFromFrameHost: action,
      create: action,
      setTraversalFromAPIResponse: action,
      setTraversalResponse: action,
      handlePathFormFieldChange: action,
      handleMultiplePathFormFieldChanges: action,
      addItemToPathList: action,
      removeItemFromPathList: action,
      updateItemFromPathList: action,
      patch: action,
      finalize: action,
      removeErrorsForPath: action,
      put: action,
      resume: action,
      setProductId: action
    })

    this.rootStore = rootStore
    this.data = getInitialFormValues(this.rootStore.urlParams)
    this.apolloClient = getClient('apollo')
    this.isTraversalCreationInProgress = false
    this.setInterceptors()
  }

  // TODO CAT-3051 We need to refactor this, this is ugly af
  setFormApi = (form: unknown) => {
    this.form = form as FormApi
  }

  setTraversalDataFromFrameHost = (data: FrameHostData) => {
    const { advertisementId, resumeHash, traversalId = null } = data

    this.traversalId = traversalId
    this.rootStore.traversalSession.nextResumeHash = resumeHash

    if (advertisementId) {
      /**
       * CAT-3887 Prefer URL param advertisementId over parent frame advertisementId
       */
      this.data.system.advertisementId =
        this.rootStore.urlParams.advertisementId || advertisementId
    }
  }

  // ---------------------------------------------------------------------------
  // API operations — HTTP calls delegated to helpers/api/traversalApi.ts
  // ---------------------------------------------------------------------------

  create = async () => {
    this.isTraversalCreationInProgress = true

    const response = await postCreateTraversal(
      this.apolloClient,
      this.data.system.advertisementId,
      this.data
    )

    this.isTraversalCreationInProgress = false
    this.setTraversalFromAPIResponse(response)

    return response
  }

  get = async (logTraversalMissingError = true) => {
    const {
      system: { advertisementId }
    } = this.data

    if (!this.traversalId && logTraversalMissingError) {
      throw new ConfigurationError(
        'The traversalId is missing.',
        advertisementId,
        this.traversalId
      )
    }

    if (this.traversalId) {
      this.setTraversalResponse(
        await getTraversal(this.apolloClient, advertisementId, this.traversalId)
      )
    }
  }

  setTraversalFromAPIResponse = async (
    response: AxiosResponse<TraversalResponse>
  ) => {
    const {
      data: { result },
      config
    } = response

    if (!result) {
      logAndTrackError(new ApiError('Response is empty', config))
      return
    }

    const { id: traversalId, resumeToken, entryPoint } = result

    if (entryPoint) {
      this.rootStore.setInitialEntryPoint(entryPoint)
    }

    this.setTraversalResponse(response)

    if (!this.traversalId) {
      this.trackTraversalEvent(traversalId || '')
    }

    this.traversalId = traversalId || null

    if (resumeToken) {
      this.rootStore.traversalSession.nextResumeHash = resumeToken
    }

    this.writeDataToFrameHost()
  }

  setProductId = (productId: number) => {
    this.data.common.productId = productId
  }

  writeDataToFrameHost = () => {
    const parentOrigin = getBrowserParentOrigin()

    if (!parentOrigin) {
      logInfo(
        'writeDataToFrameHost: skipping — not embedded in a trusted parent frame'
      )
      return
    }

    const data = {
      advertisementId: this.data.system.advertisementId,
      traversalId: this.traversalId,
      resumeHash: this.rootStore.traversalSession.nextResumeHash
    }

    logInfo(`Sending data to host: ${JSON.stringify(data, null, 2)}`)

    broadcastToParent({
      eventName: RegisteredEventName.updateCurrentImodTraversal,
      data
    })
  }

  setTraversalResponse = (response: AxiosResponse<RootObject>) => {
    const {
      status,
      data: { result },
      config
    } = response

    if (status !== HttpStatusCode.Created && status !== HttpStatusCode.Ok) {
      logAndTrackError(
        new ApiError('Traversal call returned error', config, response)
      )
      return
    }

    if (!result || !result.data) {
      logAndTrackError(
        new ApiError('Traversal does not contain any data', config, response)
      )
      return
    }

    this.data = mergeDeepRightArraySafe(this.data, result.data) as Data
  }

  patch = async (patches: Patch[]) => {
    const withConditionalPatches = addConditionalPatches(patches, this.data)
    const response = await patchTraversal(
      this.apolloClient,
      this.data.system.advertisementId,
      this.traversalId!,
      withConditionalPatches
    )

    const failuresOfAxiosResponse = getFailuresOfAxiosResponse(response)

    this.rootStore.fieldErrors.setFieldErrorsFromAxiosResponse(response, true)
    this.trackValidationSuccess(failuresOfAxiosResponse, patches)
    this.trackValidationErrors(failuresOfAxiosResponse, patches)
  }

  put = async (): Promise<AxiosResponse<RootObject>> => {
    const paths = buildPutPayloadPaths(this.data, isSmava)

    if (
      shouldResetAmountAdaption(
        this.data,
        this.rootStore.navigation.currentPageInSession
      )
    ) {
      this.data.common.amountAdaption = CreditChangeOption.nothing
    }

    const data = removePathsFromData(this.data, paths)
    this.data = data

    return putTraversal(
      this.apolloClient,
      this.data.system.advertisementId,
      this.traversalId!,
      data
    )
  }

  resume = async (resumeHash: string) => {
    const response = await postResumeTraversal(this.apolloClient, resumeHash)
    trackInAmplitude(AmplitudeEvent.ResumeHashConsumed, { resumeHash })
    this.rootStore.traversalSession.resumeData = response as unknown as Record<
      string,
      string
    >
    this.setTraversalFromAPIResponse(response)
  }

  checkTenantForEmail = async (email: string) => {
    const response = await postCheckTenantEmail(this.apolloClient, email)
    return response.data
  }

  finalize = async (
    onError?: (
      error: Error | AxiosError<RootObject> | undefined,
      checkAllPages?: boolean
    ) => void,
    checkAllPages?: boolean
  ) => {
    await executeFinalize(
      {
        rootStore: this.rootStore,
        apolloClient: this.apolloClient,
        data: this.data,
        traversalId: this.traversalId!,
        isSmava,
        setIsGoToFinalSucceeded: (v) =>
          this.rootStore.traversalSession.setIsGoToFinalSucceeded(v)
      },
      onError,
      checkAllPages
    )
  }

  goToFinal = async (
    response: AxiosResponse<{ result: { uri: Location } }>
  ) => {
    await executeGoToFinal(
      {
        rootStore: this.rootStore,
        apolloClient: this.apolloClient,
        data: this.data,
        traversalId: this.traversalId!,
        isSmava,
        setIsGoToFinalSucceeded: (v) =>
          this.rootStore.traversalSession.setIsGoToFinalSucceeded(v)
      },
      response
    )
  }

  // ---------------------------------------------------------------------------
  // Tracking — delegates to helpers/tracking/traversalTracking.ts
  // ---------------------------------------------------------------------------

  // eslint-disable-next-line class-methods-use-this
  trackValidationErrors = (failures: FailuresItem[], patches: Patch[] = []) => {
    trackValidationErrors(failures, patches)
  }

  // eslint-disable-next-line class-methods-use-this
  trackValidationSuccess = (
    failures: FailuresItem[],
    patches: Patch[] = []
  ) => {
    const {
      data: {
        system: { affiliateId, subId, refId, configurationId, advertisementId },
        debtors: {
          primary: { personId: traversalPersonId }
        }
      }
    } = this.rootStore.traversal

    trackValidationSuccess(failures, patches, {
      affiliateId,
      subId,
      refId,
      configurationId,
      advertisementId,
      traversalPersonId
    })
  }

  trackTraversalEvent = (traversalId?: string) => {
    const {
      common: { purpose, amount, term },
      system: { subId, refId, configurationId, affiliateId },
      debtors: {
        primary: { personId: traversalPersonId }
      }
    } = this.data

    trackTraversalEvent(
      {
        purpose,
        amount,
        term,
        subId,
        refId,
        configurationId,
        affiliateId,
        traversalPersonId,
        currentTraversalId: this.traversalId
      },
      traversalId
    )
  }

  setUserPropertiesOnPut = async () => {
    await sendUserPropertiesOnPut(
      this.data,
      () => this.rootStore.page.getAllPathsOnPage() as Promise<string[]>
    )
  }

  // ---------------------------------------------------------------------------
  // Payload helpers — delegates to helpers/payload/traversalPayload.ts
  // ---------------------------------------------------------------------------

  removablePathsFromPayload = (): string[] =>
    getRemovablePaths(this.data, isSmava)

  removePathsFromPayload = (paths: string[]): Data =>
    removePathsFromData(this.data, paths)

  setDacTransactionDetails = (dacTransactionId: string) => {
    const { payoutAccountHolder } = this.data.common
    const patches = buildDacTransactionPatches(
      dacTransactionId,
      payoutAccountHolder
    )
    if (patches.length) {
      this.handleMultiplePathFormFieldChanges(patches)
    }
  }

  // ---------------------------------------------------------------------------
  // Core form-field state management (MobX actions)
  // ---------------------------------------------------------------------------

  initReactions() {
    reactions.forEach((getReaction) => {
      const [conditionFn, reactionFn] = getReaction(this)
      reaction(conditionFn, reactionFn)
    })
  }

  handlePathFormFieldChange = <T = string>(
    path: string,
    fieldValue?: T | T[],
    isTraversalPatched = true
  ) => {
    const { data, patch, value } = applyFieldChange(path, fieldValue, this.data)

    this.data = data

    if (this.form) {
      this.form.change(path, value)
    }

    if (isTraversalPatched && !this.isTraversalCreationInProgress) {
      this.patch([patch])
    }
  }

  handleMultiplePathFormFieldChanges = <T>(
    payload: FieldChange<T>[],
    isTraversalPatched = true
  ) => {
    const { data, patches, valuesByPath } = applyMultipleFieldChanges(
      payload,
      this.data
    )

    this.data = data

    if (this.form) {
      Object.entries(valuesByPath).forEach(([path, value]) => {
        this.form!.change(path, value)
      })
    }

    if (isTraversalPatched && !this.isTraversalCreationInProgress) {
      this.patch(patches)
    }
  }

  addItemToPathList<P>(
    path: string,
    item: P,
    counterPath: string,
    patch = true
  ): void {
    const items = buildListWithAddedItem<P>(path, item, counterPath, this.data)
    this.handlePathFormFieldChange(path, items, patch)
    this.handlePathFormFieldChange(counterPath, items.length)
  }

  removeItemFromPathList<P>(
    path: string,
    counterPath: string,
    patch = true
  ): void {
    const items = buildListWithRemovedItem<P>(path, counterPath, this.data)
    this.handlePathFormFieldChange(counterPath, items.length)
    this.handlePathFormFieldChange(path, items, patch)
  }

  getItemFromPathList = <T = string>(
    path: string,
    idx: number
  ): T | undefined => pickListItem<T>(path, idx, this.data)

  updateItemFromPathList = <T extends Record<string, unknown>>(
    path: string,
    idx: number,
    item: T,
    isTraversalPatched = true
  ): void =>
    this.handlePathFormFieldChange(`${path}.${idx}`, item, isTraversalPatched)

  updateDataInTraversal = (data: Record<string, unknown>) => {
    runInAction(() => {
      this.data = mergeDeepRightArraySafe(this.data, data) as Data
    })
  }

  overrideDataInTraversal = (data: Data | Record<string, unknown>) => {
    runInAction(() => {
      this.data = data as unknown as Data
    })
  }

  // ---------------------------------------------------------------------------
  // Validation / error helpers
  // ---------------------------------------------------------------------------

  removeErrorsForPath = (path: string): void => {
    this.rootStore.fieldErrors.removeErrors(path)
  }

  removeErrorsForMultiplePaths = (paths: string[]): void => {
    paths.forEach((path) => this.rootStore.fieldErrors.removeErrors(path))
  }

  getErrorsForPath = (path: string) => this.rootStore.fieldErrors.get(path)

  getFieldValueByPath = <T = string>(path: string): T | undefined =>
    pick(path, this.data)

  acceptTermsAndConditions = () =>
    this.handlePathFormFieldChange('common.hasAcceptedTerms', true, false)

  hasAcceptedTermsAndConditions = () => this.data.common.hasAcceptedTerms
}

export default TraversalStore
